import { computed, ref } from 'vue';
import { httpService, type HttpError } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';

// Import side-effect: registers the contracts with the runtime registry.
import '../contracts/Clientes_AcessoPortalResponse';
import '../contracts/Clientes_CriarAcessoPortalInput';
import '../contracts/Clientes_CriarAcessoPortalResponse';
import '../contracts/Clientes_ResetarAcessoPortalInput';
import '../contracts/Clientes_ResetarAcessoPortalResponse';

import type { Clientes_AcessoPortalResponseDados } from '../contracts/Clientes_AcessoPortalResponse';
import type { Clientes_CriarAcessoPortalInput } from '../contracts/Clientes_CriarAcessoPortalInput';
import type { Clientes_CriarAcessoPortalResponseDados } from '../contracts/Clientes_CriarAcessoPortalResponse';
import type { Clientes_ResetarAcessoPortalInput } from '../contracts/Clientes_ResetarAcessoPortalInput';
import type { Clientes_ResetarAcessoPortalResponseDados } from '../contracts/Clientes_ResetarAcessoPortalResponse';

/**
 * useClienteAcessoPortal — staff-side portal-account provisioning for one cliente.
 *
 * Owns the three endpoints the staff SPA may call about a customer's login on
 * my.newshoes.com:
 *   GET  clientes/acesso-portal/{cliente_id}   — current state (safe to repeat)
 *   POST clientes/criar-acesso-portal          — provision (idempotent on the document)
 *   POST clientes/resetar-acesso-portal        — reset (invalidates + reissues, ADR-0017)
 *
 * NOT the /customer-portal/v1 family: those bypass the franchise filter and are
 * callable only by the portal BFF. These resolve the cliente through the
 * normal filter. See skill `my-newshoes-portal-integration`.
 *
 * The value returned by POST create/reset is an ACTIVATION CODE, not a password
 * — it is derived from the document and expires in 72 h. It exists only in
 * those two responses: the GET deliberately never carries it, so a re-read
 * cannot hand out a way into someone else's account. That asymmetry is why
 * `credential` is a separate ref from `account` and is never populated by
 * `loadStatus`.
 *
 * Per-caller instance (one open modal = one cliente), so state is NOT module-scope.
 */

/** UI-facing state, derived from the account record. */
export type AcessoPortalState =
  | 'idle'
  | 'loading'
  | 'none'
  | 'pending_activation'
  | 'active'
  | 'blocked'
  | 'disabled'
  | 'other'
  | 'error';

/**
 * Whether a push notification would actually reach this cliente right now —
 * the answer `state` alone cannot give, because an `active` account can still
 * have zero subscribed devices (the customer never turned on notifications on
 * their phone).
 *
 *   'no_account'    — nothing provisioned yet.
 *   'not_activated' — provisioned, but the customer never signed in.
 *   'inbox_only'    — signed in, zero subscribed devices: a push is stored and
 *                      waits where the customer may never look; the phone
 *                      will not buzz.
 *   'reachable'     — signed in with at least one subscribed device.
 *   'undetermined'  — every other case: account blocked/disabled/other status,
 *                      or (for an `active` account) `pushDevices` itself is
 *                      `null`. NEVER collapse this into `inbox_only` — that
 *                      would accuse the customer of something we don't know.
 */
export type PushReachability =
  | 'no_account'
  | 'not_activated'
  | 'inbox_only'
  | 'reachable'
  | 'undetermined';

/** Portal account for one cliente, datetimes converted at this boundary (Typed Data Layer). */
export interface AcessoPortalAccount {
  status: string;
  createdAt: Date | null;
  activatedAt: Date | null;
  credentialExpiresAt: Date | null;
  lastSeenAt: Date | null;
  blockedUntil: Date | null;
  /**
   * Subscribed push devices on the customer's portal account. `push_devices`
   * is OPTIONAL on the wire (older API build, or a create/reset response that
   * never declares it), so `null` means UNDETERMINED — NEVER read as zero.
   * Only a definite `0` means "signed in, but no device ever subscribed."
   * See `pushReachability` below and the same rule at `subscriptionIsMine`
   * in the portal's own pushState.ts.
   */
  pushDevices: number | null;
}

/**
 * The one-time activation code, available ONLY right after a successful create.
 *
 * Named `code`, not `value` as on the wire: staff-facing copy must call it an
 * activation code so nobody treats a document-derived, 72 h string as a
 * password (skill `my-newshoes-portal-integration`, "Account provisioning").
 */
export interface AcessoPortalCredential {
  code: string;
  expiresAt: Date | null;
}

/**
 * A failed call, decomposed so the component can pick a message.
 *
 * The backend sends `{success:false, error:"<code>: <sentence>"}` with the HTTP
 * status carrying the category (422 record, 429 rate limit, 503 feature off).
 * `detail` is that sentence — already staff-readable Portuguese written next to
 * the rule that rejected the request, so it beats anything phrased here. It is
 * empty when the failure never reached the controller (gateway HTML, timeout),
 * which is why the component still needs status-keyed fallbacks.
 */
export interface AcessoPortalFailure {
  status: number;
  code: string;
  detail: string;
}

const ENVELOPE_ERROR_RE = /^\s*([a-z0-9_]+)\s*:\s*(.+)$/is;

function parseFailure(err: unknown): AcessoPortalFailure {
  const httpErr = err as HttpError;
  const envelope = httpErr?.data as { error?: unknown } | undefined;
  const raw = typeof envelope?.error === 'string' ? envelope.error : '';
  const match = ENVELOPE_ERROR_RE.exec(raw);

  return {
    status: httpErr?.status ?? 0,
    code: match ? match[1] : '',
    detail: match ? match[2].trim() : raw.trim(),
  };
}

export function useClienteAcessoPortal() {
  const { fromApiDateTimeFormat } = useDateFormatter();

  const isLoading = ref(false);
  const isCreating = ref(false);
  const isResetting = ref(false);
  const account = ref<AcessoPortalAccount | null>(null);
  const credential = ref<AcessoPortalCredential | null>(null);
  const failure = ref<AcessoPortalFailure | null>(null);
  /**
   * True only after the backend answered `exists:false`. Distinct from
   * `account === null`, which is also the not-asked-yet and the failed-to-ask
   * state — offering "create" in either of those would be provisioning blind.
   */
  const noAccount = ref(false);

  /**
   * What the panel should render. `other` covers a status the portal grew that
   * this build does not know: an account exists, so never offer to create one.
   *
   * A known answer OUTRANKS `failure`, so a create that failed with 422/429
   * leaves the button on screen and clickable next to the reason — the whole
   * point of the failed call is that staff fix the record and press it again.
   * `error` is reserved for the case where nothing is known, where offering to
   * provision would mean provisioning blind.
   */
  const state = computed<AcessoPortalState>(() => {
    if (isLoading.value) return 'loading';

    if (account.value !== null) {
      switch (account.value.status) {
        case 'pending_activation':
        case 'active':
        case 'blocked':
        case 'disabled':
          return account.value.status;
        default:
          return 'other';
      }
    }

    if (noAccount.value) return 'none';
    if (failure.value) return 'error';
    return 'idle';
  });

  /** Only a confirmed "no account yet" offers provisioning. */
  const canCreate = computed(() => state.value === 'none');

  /**
   * See `PushReachability` above. Deliberately narrow: only an `active`
   * account with a KNOWN device count resolves to `inbox_only`/`reachable` —
   * everything else, including an active account whose `pushDevices` is
   * `null`, falls to `undetermined`.
   */
  const pushReachability = computed<PushReachability>(() => {
    if (state.value === 'none') return 'no_account';
    if (state.value === 'pending_activation') return 'not_activated';
    if (state.value === 'active') {
      const devices = account.value?.pushDevices ?? null;
      if (devices === null) return 'undetermined';
      return devices > 0 ? 'reachable' : 'inbox_only';
    }
    return 'undetermined';
  });

  function toAccount(
    dados:
      | Clientes_AcessoPortalResponseDados
      | Clientes_CriarAcessoPortalResponseDados
      | Clientes_ResetarAcessoPortalResponseDados,
  ): AcessoPortalAccount {
    // Cast to Partial: the reset response (Clientes_ResetarAcessoPortalResponseDados)
    // does not declare created_at/activated_at/credential_expires_at/last_seen_at/
    // blocked_until at all (it has reissued_at instead), same as the create response
    // already lacking most of these — each field that its response omits just comes
    // back null here, exactly like the pre-existing create behaviour below.
    const acesso = dados as Partial<Clientes_AcessoPortalResponseDados>;
    return {
      status: dados.status ?? '',
      createdAt: fromApiDateTimeFormat(acesso.created_at ?? null),
      activatedAt: fromApiDateTimeFormat(acesso.activated_at ?? null),
      credentialExpiresAt: fromApiDateTimeFormat(acesso.credential_expires_at ?? null),
      lastSeenAt: fromApiDateTimeFormat(acesso.last_seen_at ?? null),
      blockedUntil: fromApiDateTimeFormat(acesso.blocked_until ?? null),
      pushDevices: acesso.push_devices ?? null,
    };
  }

  /**
   * GET the current portal-account state. `exists:false` is a normal answer, not
   * a failure — it is what the create button is for.
   */
  async function loadStatus(clienteId: number): Promise<void> {
    if (!clienteId) return;

    isLoading.value = true;
    failure.value = null;
    credential.value = null;

    try {
      const resp = await httpService.get(`/index.php/clientes/acesso-portal/${clienteId}`, {
        responseContract: 'Clientes_AcessoPortalResponse',
        skipErrorDisplay: true,
      });

      const dados = resp.dados;
      account.value = dados?.exists ? toAccount(dados) : null;
      if (!dados?.exists) noAccount.value = true;

      log('useClienteAcessoPortal', 'loadStatus', 'Estado do acesso ao portal', {
        clienteId,
        exists: dados?.exists,
        status: dados?.status,
      });
    } catch (err) {
      account.value = null;
      noAccount.value = false;
      failure.value = parseFailure(err);
      log('useClienteAcessoPortal', 'loadStatus', 'Falha ao consultar o acesso ao portal', {
        clienteId,
        status: failure.value.status,
        code: failure.value.code,
      });
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * POST the provisioning request. On success the panel flips to the returned
   * status and shows the activation code — the only moment it is readable.
   *
   * Never re-throws: the button owns the feedback, and an unhandled rejection
   * would leave it spinning with nothing on screen.
   */
  async function createAccess(clienteId: number): Promise<void> {
    if (!clienteId || isCreating.value) return;

    isCreating.value = true;
    failure.value = null;

    const body: Clientes_CriarAcessoPortalInput = { cliente_id: clienteId };

    try {
      const resp = await httpService.post(`/index.php/clientes/criar-acesso-portal`, body, {
        inputContract: 'Clientes_CriarAcessoPortalInput',
        responseContract: 'Clientes_CriarAcessoPortalResponse',
        skipErrorDisplay: true,
      });

      const dados = resp.dados;
      if (!dados) {
        failure.value = { status: 0, code: '', detail: '' };
        return;
      }

      noAccount.value = false;
      account.value = toAccount(dados);

      const codigo = dados.activation_credential?.value ?? '';
      credential.value = codigo
        ? {
            code: codigo,
            expiresAt: fromApiDateTimeFormat(dados.activation_credential?.expires_at ?? null),
          }
        : null;

      // The code itself is never logged — it is the customer's way in for 72 h.
      log('useClienteAcessoPortal', 'createAccess', 'Acesso ao portal provisionado', {
        clienteId,
        alreadyExisted: dados.already_existed,
        status: dados.status,
      });
    } catch (err) {
      failure.value = parseFailure(err);
      log('useClienteAcessoPortal', 'createAccess', 'Falha ao criar o acesso ao portal', {
        clienteId,
        status: failure.value.status,
        code: failure.value.code,
      });
    } finally {
      isCreating.value = false;
    }
  }

  /**
   * POST the reset request — invalidates the outstanding credential/session and
   * mints a fresh activation code (ADR-0017). On success the panel flips to the
   * returned status and shows the new code, the same asymmetry `createAccess`
   * already documents: this is the one moment it is readable again.
   *
   * Never re-throws: the button owns the feedback, and an unhandled rejection
   * would leave it spinning with nothing on screen.
   */
  async function resetAccess(clienteId: number): Promise<void> {
    if (!clienteId || isResetting.value) return;

    isResetting.value = true;
    failure.value = null;

    const body: Clientes_ResetarAcessoPortalInput = { cliente_id: clienteId };

    try {
      const resp = await httpService.post(`/index.php/clientes/resetar-acesso-portal`, body, {
        inputContract: 'Clientes_ResetarAcessoPortalInput',
        responseContract: 'Clientes_ResetarAcessoPortalResponse',
        skipErrorDisplay: true,
      });

      const dados = resp.dados;
      if (!dados) {
        failure.value = { status: 0, code: '', detail: '' };
        return;
      }

      noAccount.value = false;
      account.value = toAccount(dados);

      const codigo = dados.activation_credential?.value ?? '';
      credential.value = codigo
        ? {
            code: codigo,
            expiresAt: fromApiDateTimeFormat(dados.activation_credential?.expires_at ?? null),
          }
        : null;

      // The code itself is never logged — it is the customer's way in for 72 h.
      log('useClienteAcessoPortal', 'resetAccess', 'Acesso ao portal resetado', {
        clienteId,
        status: dados.status,
      });
    } catch (err) {
      failure.value = parseFailure(err);
      log('useClienteAcessoPortal', 'resetAccess', 'Falha ao resetar o acesso ao portal', {
        clienteId,
        status: failure.value.status,
        code: failure.value.code,
      });
    } finally {
      isResetting.value = false;
    }
  }

  /** Wipe state when the modal closes or switches to another cliente. */
  function reset(): void {
    isLoading.value = false;
    isCreating.value = false;
    isResetting.value = false;
    account.value = null;
    noAccount.value = false;
    credential.value = null;
    failure.value = null;
  }

  return {
    // state
    state,
    canCreate,
    pushReachability,
    isLoading,
    isCreating,
    isResetting,
    account,
    credential,
    failure,
    // actions
    loadStatus,
    createAccess,
    resetAccess,
    reset,
  };
}
